import useUserUiPreferenceStorageValue from "../../hooks/useUserUiPreferenceStorageValue.js";
import React from "react";
import GeotabDayPicker from "./GeotabDayPicker.jsx";
import { fetchGeotabDashboard } from "../../api/geotabApi.js";
import { getLocalTodayDispatchDate } from "../../utils/dispatchDayCalendarUtils.js";
import GeotabBusy from "./GeotabBusy.jsx";
import GeotabDashboardOverview, { KpiStrip } from "./GeotabDashboardOverview.jsx";
import GeotabDriversTable from "./GeotabDriversTable.jsx";
import { plural } from "./geotabDashboard.js";
import { dashboardScopes, metricOf } from "./geotabDashboardSeries.js";
import { readGeotabSession, THRESHOLD_CHOICES, writeGeotabSession } from "./geotabPreferences.js";

// Tableau de bord : le bandeau des mesures principales (un clic choisit la mesure détaillée), la vue d'ensemble sur le modèle du
// Tableau de bord principal, ou chaque chauffeur comparé à la flotte. Même période que le Calibrage, journées clôturées seulement.
function shiftDate(value, days) {
  const d = new Date(`${value}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function GeotabDashboardView({ period = "day", thresholds, onPeriodChange, preferences, onPreferencesChange, onOpenView }) {
  const yesterday = shiftDate(getLocalTodayDispatchDate(), -1);
  // Même période que le Calibrage, enregistrée pour l'utilisateur ; sans choix, les 30 derniers jours.
  const [range, setRangeState] = React.useState(() => ({
    from: preferences.calibration_from || shiftDate(yesterday, -29), to: preferences.calibration_to || yesterday,
  }));
  const setRange = (next) => {
    setRangeState(next);
    onPreferencesChange({ calibration_from: next.from, calibration_to: next.to });
  };
  const [view, setViewState] = React.useState(() => (readGeotabSession().dashboard_view === "drivers" ? "drivers" : "overview"));
  const setView = (next) => {
    setViewState(next);
    writeGeotabSession({ dashboard_view: next });
  };
  // Mesure détaillée et portée (la période ou sa dernière semaine), gardées pour la session comme la vue.
  const [metricKey, setMetricKey] = React.useState(() => metricOf(readGeotabSession().dashboard_metric || "route").key);
  const [scope, setScopeState] = React.useState(() => (readGeotabSession().dashboard_scope === "week" ? "week" : "period"));
  const storedDashboard = useUserUiPreferenceStorageValue("session", "grain:geotab-session");
  React.useEffect(() => {
    const next = readGeotabSession();
    setViewState(next.dashboard_view === "drivers" ? "drivers" : "overview");
    setMetricKey(metricOf(next.dashboard_metric || "route").key);
    setScopeState(next.dashboard_scope === "week" ? "week" : "period");
  }, [storedDashboard]);
  const chooseMetric = (key) => {
    setMetricKey(key);
    writeGeotabSession({ dashboard_metric: key });
    if (view !== "overview") setView("overview");
  };
  const setScope = (next) => {
    setScopeState(next);
    writeGeotabSession({ dashboard_scope: next });
  };
  const [request, setRequest] = React.useState(0);
  const [state, setState] = React.useState({ data: null, busy: false, error: null });
  React.useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, busy: true, error: null }));
    fetchGeotabDashboard(range.from, range.to, period, thresholds)
      .then((data) => { if (!cancelled) setState({ data, busy: false, error: null }); })
      .catch((error) => { if (!cancelled) setState({ data: null, busy: false, error }); });
    return () => { cancelled = true; };
    // Les dates ne se recalculent qu'avec « Calculer », comme au Calibrage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, period, thresholds]);
  const { data, busy, error } = state;
  const scopes = React.useMemo(() => (data ? dashboardScopes(data.bar) : null), [data]);
  const shown = scope === "week" && scopes?.week ? "week" : "period";
  const tolerance = thresholds?.tolerance_min ?? 15;
  const openDay = (date) => onOpenView("comparison", { date });
  const openDriver = () => {
    writeGeotabSession({ calibration_tab: "drivers" });
    onOpenView("calibration");
  };

  return (
    <div className="geotab-dash" data-geotab-dashboard={view}>
      <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-slate-700">
        <span className="font-semibold">Du</span>
        <GeotabDayPicker label="Début de la période" value={range.from} max={range.to} onChange={(from) => setRange({ ...range, from })} />
        <span className="font-semibold">au</span>
        <GeotabDayPicker label="Fin de la période" value={range.to} min={range.from} onChange={(to) => setRange({ ...range, to })} />
        <span className="inline-flex overflow-hidden rounded-[4px] border border-slate-300" role="group" aria-label="Période du Dispatch">
          {[["day", "Jour"], ["evening", "Soir"]].map(([key, label]) => (
            <button key={key} type="button" aria-pressed={period === key} onClick={() => onPeriodChange(key)}
              className={`h-7 px-3 text-[12.5px] font-semibold ${period === key ? "bg-accent text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>{label}</button>
          ))}
        </span>
        <button type="button" disabled={busy} onClick={() => setRequest((n) => n + 1)}
          className="h-7 rounded-[4px] border border-accent bg-accent px-3 text-[12.5px] font-semibold text-white hover:border-accent-strong hover:bg-accent-strong disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400">
          Calculer
        </button>
        <span className="inline-flex overflow-hidden rounded-[4px] border border-slate-300" role="group" aria-label="Vue du tableau de bord">
          {[["overview", "Vue d'ensemble"], ["drivers", "Par chauffeur"]].map(([key, label]) => (
            <button key={key} type="button" aria-pressed={view === key} onClick={() => setView(key)}
              className={`h-7 px-3 text-[12.5px] font-semibold ${view === key ? "bg-accent text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>{label}</button>
          ))}
        </span>
        <span className="inline-flex overflow-hidden rounded-[4px] border border-slate-300" role="group" aria-label="Portée des mesures">
          {[["week", "Semaine"], ["period", data ? plural(data.days, "journée") : "Période"]].map(([key, label]) => (
            <button key={key} type="button" aria-pressed={shown === key} disabled={key === "week" && !!scopes && !scopes.week} onClick={() => setScope(key)}
              className={`h-7 px-3 text-[12.5px] font-semibold ${shown === key ? "bg-accent text-white" : "bg-white text-slate-700 hover:bg-slate-50"} disabled:bg-slate-50 disabled:text-slate-400`}>{label}</button>
          ))}
        </span>
        <label className="inline-flex items-center gap-1 font-semibold">Tolérance
          <select aria-label="Tolérance d'arrivée" value={tolerance} onChange={(e) => onPreferencesChange({ tolerance_min: Number(e.target.value) })}
            className="h-7 rounded-[4px] border border-slate-300 bg-white py-0 pl-1 pr-5 text-[12.5px] font-semibold text-slate-800">
            {THRESHOLD_CHOICES.tolerance_min.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
          </select>
        </label>
        <span className="text-[11.5px] text-slate-500">
          {data && !busy
            ? `${data.days} journée${data.days > 1 ? "s" : ""} clôturée${data.days > 1 ? "s" : ""}${data.requested_to ? ` (période limitée à 62 jours : jusqu'au ${data.to})` : ""} · ${plural(data.drivers.length, "chauffeur")}`
              + (data.days_not_closed ? ` · ${data.days_not_closed} non clôturée(s) ignorée(s)` : "")
            : "Journées clôturées seulement (photo de clôture)"}
        </span>
      </div>
      {busy && <GeotabBusy title="Calcul du tableau de bord…" detail="Analyse des journées et des trajets Geotab" />}
      {error && <p role="alert" className="rounded-[4px] border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error.message}</p>}
      {data && !busy && (
        <>
          <KpiStrip scopes={scopes} scope={shown} metricKey={view === "overview" ? metricKey : null} onMetric={chooseMetric} tolerance={tolerance} />
          {view === "drivers" ? <GeotabDriversTable data={data} onOpenDriver={openDriver} /> : (
            <GeotabDashboardOverview data={data} scopes={scopes} scope={shown} metric={metricOf(metricKey)}
              onOpenDay={openDay} onOpenView={onOpenView} onDrivers={() => setView("drivers")} />
          )}
        </>
      )}
    </div>
  );
}
